<template>
  <div id="email_templates" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Email Templates</h1>
      <a-button class="primary_medium" @click="showCreateDrawer"
        >Create Template</a-button
      >
    </div>

    <EmptyScreen
      v-if="templates.length === 0"
      source="email_template"
      text="No templates created yet!"
    />

    <CustomTable
      v-else
      :headers="templateHeaders"
      :data="templates"
      :pagination="pagination"
      :show-actions-column="true"
      :actions="actions"
      @update:pagination="handlePageChange"
    />

    <EmailTemplateDrawer
      v-model:visible="drawerVisible"
      :mode="drawerMode"
      :template-form="templateForm"
      @save="handleDrawerSave"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import EmailTemplateDrawer from "./EmailTemplateDrawer.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const drawerVisible = ref(false);
const drawerMode = ref("create");
const noTemplateIcon = imageNames.NO_TEMPLATE_GREY;
const templates = ref([]);
const templateForm = reactive({
  id: null,
  name: "",
  subject: "",
  body: "",
  variables: [],
});
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
});

const showCreateDrawer = () => {
  drawerMode.value = "create";
  Object.assign(templateForm, {
    id: null,
    name: "",
    subject: "",
    body: "",
    variables: [],
  });
  drawerVisible.value = true;
};

const handleDrawerSave = async (formData) => {
  try {
    let response;
    if (drawerMode.value === "create") {
      response = await DashboardService.createEmailTemplate(formData);
    } else {
      response = await DashboardService.updateEmailTemplate(
        formData.id,
        formData,
      );
    }
    if (response.success) {
      message.success(
        `Template ${drawerMode.value === "create" ? "created" : "updated"} successfully`,
      );
      drawerVisible.value = false;
      fetchTemplates();
    } else {
      message.error(`Failed to ${drawerMode.value} template`);
    }
  } catch (error) {
    console.error(`Error ${drawerMode.value}ing template:`, error);
    message.error(
      `An error occurred while ${drawerMode.value}ing the template`,
    );
  }
};

const templateHeaders = [
  { label: "Template Name", key: "name", width: 4 },
  { label: "Subject", key: "subject", width: 8 },
];

const actions = [
  {
    label: "Edit",
    handler: (row) => editTemplate(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => deleteTemplate(row.id),
    icon: imageNames.DELETE_GREY,
  },
];

const fetchTemplates = async () => {
  try {
    const response = await DashboardService.getEmailTemplates();
    if (response.success) {
      templates.value = response.templates;
    } else {
      message.error("Failed to fetch templates");
    }
  } catch (error) {
    console.error("Error fetching templates:", error);
    message.error("An error occurred while fetching templates");
  }
};

const handlePageChange = (newPagination) => {
  pagination.current = newPagination.current;
  pagination.pageSize = newPagination.pageSize;
  fetchTemplates();
};

const editTemplate = (template) => {
  drawerMode.value = "edit";
  Object.assign(templateForm, { ...template });
  drawerVisible.value = true;
};

const deleteTemplate = async (id) => {
  try {
    const response = await DashboardService.deleteEmailTemplate(id);
    if (response.success) {
      message.success("Template deleted successfully");
      fetchTemplates().then().catch();
    } else {
      message.error("Failed to delete template");
    }
  } catch (error) {
    console.error("Error deleting template:", error);
    message.error("An error occurred while deleting the template");
  }
};

onMounted(() => {
  fetchTemplates();
});
</script>
